import React, { useState } from 'react';

import { Alert, Button, Group, Paper, Skeleton, Title, useMantineTheme } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconCreditCard, IconExclamationCircle } from '@tabler/icons-react';
import AddPaymentMethodDrawer from 'components/payment-methods/AddPaymentMethodDrawer';
import BacsDebitPaymentMethodCard from 'components/payment-methods/BacsDebitPaymentMethodCard';
import BankAccountPaymentMethodCard from 'components/payment-methods/BankAccountPaymentMethodCard';
import CreditCardPaymentMethodCard from 'components/payment-methods/CreditCardPaymentMethodCard';
import EditPaymentMethodForm from 'components/payment-methods/EditPaymentMethodForm';
import UnlockPaymentMethodModal from 'components/payment-methods/UnlockPaymentMethodModal';
import { useAuth } from 'context/auth-context';
import dayjs from 'dayjs';
import useAthlete from 'hooks/athlete/useAthlete';
import usePaymentMethods from 'hooks/paymentMethod/usePaymentMethods';
import { getPaymentMethodName } from 'utils/formatters';

const PaymentMethods = (): JSX.Element => {
  const theme = useMantineTheme();
  const { session } = useAuth();

  const [paymentMethodId, setPaymentMethodId] = useState<string>('');
  const [isPaymentMethodFormOpened, { open: openPaymentMethodForm, close: closePaymentMethodForm }] =
    useDisclosure(false);
  const [isEditFormOpen, { open: openEditForm, close: closeEditForm }] = useDisclosure(false);
  const [isUnlockModalOpen, { open: openUnlockModal, close: closeUnlockModal }] = useDisclosure(false);

  const { data, isSuccess, isError, isLoading } = usePaymentMethods({
    athleteId: session?.athlete?.athleteId,
  });

  const { data: athleteData } = useAthlete(session?.athlete?.athleteId);

  const handleAddPaymentMethodClick = (): void => {
    openPaymentMethodForm();
  };

  const handleOpenEditPaymentMethodForm = (id: string): void => {
    setPaymentMethodId(id);
    openEditForm();
  };

  const handleCloseEditPaymentMethodForm = (): void => {
    setPaymentMethodId('');
    closeEditForm();
  };

  const handleOpenPaymentMethodUnlockModal = (id: string): void => {
    setPaymentMethodId(id);
    openUnlockModal();
  };

  return (
    <>
      <Paper radius='lg' p='md' shadow='md' withBorder>
        <Group position='apart' pb='md' noWrap>
          <Title order={4} color='dark.3'>
            Payment Methods
          </Title>
          <Button compact onClick={handleAddPaymentMethodClick} loading={isLoading}>
            Add
          </Button>
        </Group>

        {isLoading && <Skeleton h='xl' />}

        {isError && (
          <Alert
            radius='lg'
            sx={{ border: `1px solid ${theme.colors.gray[2]}` }}
            icon={<IconExclamationCircle size={theme.fontSizes.md} />}
            title='Error'
            color='red'
          >
            Something went wrong while fetching data!
          </Alert>
        )}

        {isSuccess &&
          data &&
          data.pages.map(page => {
            return page.data.results.length ? (
              page.data.results.map(result => {
                if (result.type === 'CARD') {
                  const expMonth = result.metadata?.expMonth;
                  const expYear = result.metadata?.expYear;
                  const expirationDate =
                    expMonth && expYear ? dayjs(`${expYear}/${expMonth}`).format('MM/YY') : '';
                  return (
                    <CreditCardPaymentMethodCard
                      country={result.metadata?.billingDetails?.address?.country}
                      key={result.paymentMethodId}
                      isDefault={result.isDefault}
                      name={getPaymentMethodName(result)}
                      zip={result.metadata?.billingDetails?.address?.postalCode || ''}
                      expirationDate={expirationDate}
                      athlete={athleteData?.data}
                      spacing='xs'
                      paymentMethodId={result.paymentMethodId}
                      openEditPaymentMethodForm={handleOpenEditPaymentMethodForm}
                      isLocked={result.isLocked}
                      handleUnlock={handleOpenPaymentMethodUnlockModal}
                    />
                  );
                }

                if (result.type === 'US BANK ACCOUNT' || result.type === 'ACSS DEBIT') {
                  return (
                    <BankAccountPaymentMethodCard
                      type={result.type}
                      isDefault={result.isDefault}
                      key={result.paymentMethodId}
                      name={getPaymentMethodName(result)}
                      last4={result.metadata?.last4 || ''}
                      routingNumber={result.metadata?.routingNumber || ''}
                      paymentMethodId={result.paymentMethodId}
                      athlete={athleteData?.data}
                      openEditPaymentMethodForm={handleOpenEditPaymentMethodForm}
                      isLocked={result.isLocked}
                      handleUnlock={handleOpenPaymentMethodUnlockModal}
                    />
                  );
                }

                if (result.type === 'BACS DEBIT') {
                  return (
                    <BacsDebitPaymentMethodCard
                      key={result.paymentMethodId}
                      name={result.metadata?.billingDetails?.name || ''}
                      last4={result.metadata?.last4 || ''}
                      sortCode={result.metadata?.sortCode || ''}
                      isDefault={result.isDefault}
                      paymentMethodId={result.paymentMethodId}
                      isLocked={result.isLocked}
                      failureMessage={result.failureMessage}
                      athlete={athleteData?.data}
                      openEditPaymentMethodForm={handleOpenEditPaymentMethodForm}
                      handleUnlock={handleOpenPaymentMethodUnlockModal}
                    />
                  );
                }

                return null;
              })
            ) : (
              <Alert
                radius='lg'
                sx={{ border: `1px solid ${theme.colors.gray[2]}` }}
                icon={<IconCreditCard size={theme.fontSizes.md} />}
                title='No payment methods'
                color='gray'
              >
                You have not added any payment methods.
              </Alert>
            );
          })}
      </Paper>

      {session?.athlete?.athleteId && session?.gym?.gymId && (
        <AddPaymentMethodDrawer
          athleteId={session.athlete.athleteId}
          gymId={session.gym.gymId}
          opened={isPaymentMethodFormOpened}
          closeHandler={closePaymentMethodForm}
        />
      )}

      <EditPaymentMethodForm
        paymentMethodId={paymentMethodId}
        opened={isEditFormOpen}
        closeHandler={handleCloseEditPaymentMethodForm}
      />

      <UnlockPaymentMethodModal
        paymentMethodId={paymentMethodId}
        opened={isUnlockModalOpen}
        close={closeUnlockModal}
        userRole='athlete'
      />
    </>
  );
};

export default PaymentMethods;
